// Interactive dialogs on the page (shared/API.md §36.4) + a card press's goto_location (§36.5).
//
// The worker decides which `open_dialog` is THIS client's (trigger ids) and sends it as the `dialog`
// signal with the conversation + team it submits against; this host shows one at a time (a second
// waits for the first to close), validates with core/interactiveDialog before any request, and
// keeps the form open — with what was typed — on every refusal. Mounted once by the Workspace.
import { useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { data } from '../../../bridge/client';
import type { ChannelLinkTarget } from '../../../core/channelLinks';
import {
  fieldError,
  initialValues,
  maxLength,
  submitLabel,
  validate,
  type Dialog as DialogModel,
  type DialogContext,
  type DialogElement,
  type DialogFieldError,
  type DialogValue,
  type DialogValues,
} from '../../../core/interactiveDialog';
import type { UserRecord } from '../../../core/records';
import { Icon } from '../../../design/icons';
import { t, tf } from '../../../i18n/i18n';
import { MessageBody } from '../../conversation/MessageBody';
import { Button, Dialog } from '../../kit/Kit';
import { ShellContext } from '../../shell/ShellContext';
import { followGoto } from './goto';
import './pluginUi.css';

type Open = { dialog: DialogModel; context: DialogContext; key: number };

const INTRO_CTX = { knownUsernames: new Set<string>(), currentUsername: null, customEmoji: {} };

export function errorText(e: DialogFieldError): string {
  switch (e.code) {
    case 'tooShort':
      return tf('web.dialog.error.tooShort', String(e.n));
    case 'tooLong':
      return tf('web.dialog.error.tooLong', String(e.n));
    default:
      return t(`web.dialog.error.${e.code}`);
  }
}

/** The Workspace's one host: the dialog queue and the `goto` signal. */
export function DialogHost() {
  const shell = useContext(ShellContext);
  const [queue, setQueue] = useState<Open[]>([]);
  const seq = useRef(0);
  const shellRef = useRef(shell);
  shellRef.current = shell;

  useEffect(
    () =>
      data.onSignal((s) => {
        if (s.kind === 'dialog') setQueue((q) => [...q, { dialog: s.dialog, context: s.context, key: ++seq.current }]);
        else if (s.kind === 'goto') {
          void data.call('composeConfig').then((c) => followGoto(shellRef.current, s.location, c.siteUrl));
        }
      }),
    [],
  );

  const head = queue[0];
  if (!head) return null;
  const context = { channelId: head.context.channelId, teamId: head.context.teamId || shell?.team.id || '' };
  return (
    <InteractiveDialog
      key={head.key}
      dialog={head.dialog}
      context={context}
      onReplace={(next) => setQueue((q) => (q[0]?.key === head.key ? [{ ...q[0], dialog: next, key: ++seq.current }, ...q.slice(1)] : q))}
      onClosed={() => setQueue((q) => q.filter((x) => x.key !== head.key))}
    />
  );
}

function InteractiveDialog({ dialog, context, onReplace, onClosed }: { dialog: DialogModel; context: DialogContext; onReplace(next: DialogModel): void; onClosed(): void }) {
  const [values, setValues] = useState<DialogValues>(() => initialValues(dialog.elements));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [general, setGeneral] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (name: string, value: DialogValue) => {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors(({ [name]: _gone, ...rest }) => rest);
  };

  const cancel = () => {
    if (busy) return;
    onClosed();
    void data.call('cancelDialog', { dialog, context });
  };

  const submit = async () => {
    if (busy) return;
    const local = validate(dialog.elements, values);
    if (Object.keys(local).length) {
      setErrors(Object.fromEntries(Object.entries(local).map(([k, e]) => [k, errorText(e)])));
      setGeneral('');
      return;
    }
    setBusy(true);
    setGeneral('');
    const r = await data.call('submitDialog', { dialog, values, context }).catch(() => ({ kind: 'failed' as const }));
    setBusy(false);
    switch (r.kind) {
      case 'done':
        onClosed();
        return;
      case 'form':
        onReplace(r.dialog);
        return;
      case 'errors':
        setErrors(r.fieldErrors);
        setGeneral(r.error);
        return;
      case 'failed':
        setGeneral(t('web.dialog.failed'));
        return;
    }
  };

  const footer = (
    <>
      <Button kind="secondary" onClick={cancel} disabled={busy}>
        {t('web.dialog.cancel')}
      </Button>
      <Button kind="primary" onClick={() => void submit()} disabled={busy}>
        {busy ? <span className="pd-spinner" aria-hidden /> : null}
        {submitLabel(dialog, t('web.dialog.submit'))}
      </Button>
    </>
  );

  return (
    <Dialog title={dialog.title} onClose={cancel} footer={footer} width={520}>
      <form
        className="pd-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {dialog.iconUrl || dialog.introductionText ? (
          <div className="pd-intro">
            {dialog.iconUrl ? <img className="pd-icon" src={dialog.iconUrl} alt="" referrerPolicy="no-referrer" /> : null}
            {dialog.introductionText ? (
              <div className="pd-intro-text">
                <MessageBody message={dialog.introductionText} ctx={INTRO_CTX} />
              </div>
            ) : null}
          </div>
        ) : null}
        {general ? (
          <div className="pd-general" role="alert">
            {general}
          </div>
        ) : null}
        {dialog.elements.map((e) => (
          <Field key={e.name} element={e} value={values[e.name]} error={errors[e.name] ?? ''} teamId={context.teamId} onChange={(v) => set(e.name, v)} onBlur={() => {
            const err = fieldError(e, values[e.name]);
            // Too long is the only error worth saying before Submit (the field caps it anyway).
            if (err?.code === 'tooLong') setErrors((x) => ({ ...x, [e.name]: errorText(err) }));
          }} />
        ))}
        {/* Enter in a one-line field submits, like every form. */}
        <button type="submit" hidden aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}

function Label({ element, htmlFor }: { element: DialogElement; htmlFor?: string }) {
  return (
    <label className="pd-label" htmlFor={htmlFor}>
      {element.displayName}
      {element.optional ? <span className="pd-optional">{t('web.dialog.optional')}</span> : null}
    </label>
  );
}

function Help({ element, error }: { element: DialogElement; error: string }) {
  if (error) {
    return (
      <div className="pd-help error" role="alert">
        {error}
      </div>
    );
  }
  return element.helpText ? <div className="pd-help">{element.helpText}</div> : null;
}

function inputType(e: DialogElement): string {
  switch (e.subtype) {
    case 'email':
    case 'tel':
    case 'url':
    case 'password':
      return e.subtype;
    default:
      return 'text';
  }
}

function Field({ element: e, value, error, teamId, onChange, onBlur }: { element: DialogElement; value: DialogValue | undefined; error: string; teamId: string; onChange(v: DialogValue): void; onBlur(): void }) {
  const id = `pd-${e.name}`;
  const text = typeof value === 'string' ? value : '';
  let control: ReactNode;
  switch (e.type) {
    case 'textarea':
      control = <textarea id={id} className="pd-input pd-textarea" value={text} placeholder={e.placeholder} maxLength={maxLength(e)} rows={4} onChange={(x) => onChange(x.target.value)} onBlur={onBlur} aria-invalid={!!error} />;
      break;
    case 'bool':
      return (
        <div className={`pd-field pd-bool${error ? ' invalid' : ''}`}>
          <Label element={e} htmlFor={id} />
          <label className="pd-check">
            <input id={id} type="checkbox" checked={value === true} onChange={(x) => onChange(x.target.checked)} aria-invalid={!!error} />
            <span>{e.placeholder}</span>
          </label>
          <Help element={e} error={error} />
        </div>
      );
    case 'radio':
      return (
        <fieldset className={`pd-field pd-radio${error ? ' invalid' : ''}`}>
          <legend className="pd-label">
            {e.displayName}
            {e.optional ? <span className="pd-optional">{t('web.dialog.optional')}</span> : null}
          </legend>
          {e.options.map((o) => (
            <label key={o.value} className="pd-check">
              <input type="radio" name={id} value={o.value} checked={text === o.value} onChange={() => onChange(o.value)} />
              <span>{o.text}</span>
            </label>
          ))}
          <Help element={e} error={error} />
        </fieldset>
      );
    case 'select':
      if (e.dataSource === 'users') control = <UserPicker id={id} value={text} teamId={teamId} placeholder={e.placeholder} onChange={onChange} />;
      else if (e.dataSource === 'channels') control = <ChannelPicker id={id} value={text} teamId={teamId} placeholder={e.placeholder} onChange={onChange} />;
      else
        control = (
          <select id={id} className="pd-input pd-select" value={text} onChange={(x) => onChange(x.target.value)} aria-invalid={!!error}>
            <option value="">{e.placeholder || t('web.dialog.select.none')}</option>
            {e.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.text}
              </option>
            ))}
          </select>
        );
      break;
    default:
      control = (
        <input
          id={id}
          className="pd-input"
          type={inputType(e)}
          inputMode={e.subtype === 'number' ? 'decimal' : undefined}
          value={text}
          placeholder={e.placeholder}
          maxLength={maxLength(e)}
          onChange={(x) => onChange(x.target.value)}
          onBlur={onBlur}
          aria-invalid={!!error}
          autoComplete="off"
        />
      );
  }
  return (
    <div className={`pd-field${error ? ' invalid' : ''}`}>
      <Label element={e} htmlFor={id} />
      {control}
      <Help element={e} error={error} />
    </div>
  );
}

/** `data_source: "users"`: the local roster first, then the server's people search (§19.1); shows `@username`. */
function UserPicker({ id, value, teamId, placeholder, onChange }: { id: string; value: string; teamId: string; placeholder: string; onChange(v: string): void }) {
  const [term, setTerm] = useState('');
  const [found, setFound] = useState<UserRecord[]>([]);
  const [picked, setPicked] = useState<UserRecord | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!value) return setPicked(null);
    if (picked?.id === value) return;
    void data.call('peopleLocal', '', 2000).then((r) => setPicked(r.users.find((u) => u.id === value) ?? null));
  }, [value, picked?.id]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    void data.call('peopleLocal', term, 8).then((r) => alive && setFound(r.users));
    const timer = setTimeout(() => {
      if (!term.trim() || !teamId) return;
      void data.call('peopleSearch', teamId, term).then((r) => {
        if (!alive || !('users' in r)) return;
        setFound((local) => {
          const seen = new Set(local.map((u) => u.id));
          return [...local, ...r.users.filter((u) => !seen.has(u.id))].slice(0, 12);
        });
      });
    }, 250);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [term, open, teamId]);

  const results = useMemo(() => found.filter((u) => u.deleteAt === 0), [found]);

  if (value && picked) {
    return (
      <div className="pd-picked">
        <span>@{picked.username}</span>
        <button type="button" className="icon-button" onClick={() => onChange('')} aria-label={t('web.dialog.users.clear')} title={t('web.dialog.users.clear')}>
          <Icon name="x" size={14} />
        </button>
      </div>
    );
  }
  return (
    <div className="pd-combo">
      <input
        id={id}
        className="pd-input"
        value={term}
        placeholder={placeholder || t('web.dialog.users.search')}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onChange={(x) => setTerm(x.target.value)}
        autoComplete="off"
        role="combobox"
        aria-expanded={open && results.length > 0}
      />
      {open && results.length ? (
        <div className="pd-options" role="listbox">
          {results.map((u) => (
            <button
              type="button"
              key={u.id}
              role="option"
              aria-selected={false}
              onMouseDown={(x) => {
                x.preventDefault();
                setPicked(u);
                setTerm('');
                setOpen(false);
                onChange(u.id);
              }}
            >
              <span className="pd-option-main">@{u.username}</span>
              {u.firstName || u.lastName ? <span className="pd-option-sub">{`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim()}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** `data_source: "channels"`: the team's channels the reader is in, from the store; shows the display name. */
function ChannelPicker({ id, value, teamId, placeholder, onChange }: { id: string; value: string; teamId: string; placeholder: string; onChange(v: string): void }) {
  const [channels, setChannels] = useState<ChannelLinkTarget[]>([]);
  useEffect(() => {
    if (!teamId) return;
    void data.call('channelCatalogue', teamId).then((c) => setChannels([...c.joined].sort((a, b) => a.displayName.localeCompare(b.displayName))));
  }, [teamId]);
  return (
    <select id={id} className="pd-input pd-select" value={value} onChange={(x) => onChange(x.target.value)}>
      <option value="">{placeholder || t('web.dialog.channels.none')}</option>
      {channels.map((c) => (
        <option key={c.channelId} value={c.channelId}>
          {c.displayName}
        </option>
      ))}
    </select>
  );
}
